import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { login } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(formData.username, formData.password);
      localStorage.setItem('adminToken', response.data.token);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-coral-50 via-orange-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <Link to="/" className="flex flex-col items-center justify-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Ixoraly Logo" 
            className="h-20 w-auto mb-3"
          />
          <span className="text-3xl font-bold text-gray-900">Ixoraly</span>
        </Link>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <span className="text-xs bg-coral-100 text-coral-700 px-4 py-2 rounded-full font-medium">Admin Portal</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="admin-login-heading">
              Admin Login
            </h1>
            <p className="text-sm text-gray-600">
              Sign in to manage press releases
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <Input
                id="username"
                name="username"
                type="text"
                data-testid="login-username-input"
                value={formData.username}
                onChange={handleChange}
                required
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                data-testid="login-password-input"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-coral-600 hover:bg-coral-700 text-white"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <span className="flex items-center justify-center space-x-2">
                  <LogIn size={18} />
                  <span>Sign In</span>
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-coral-600 hover:text-coral-700">
              Back to Website
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Default credentials: username: <code className="bg-gray-100 px-2 py-1 rounded">admin</code> password: <code className="bg-gray-100 px-2 py-1 rounded">admin123</code></p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;